import {createI18n} from 'vue-i18n';
import {readStoredLocale, resolvePreferredLocale} from './locale-choice';
import {localizeDocsPath} from './docs-path';
import {localizeHref} from './locale-path';
import {DEFAULT_LOCALE, type Locale} from './locales';
import {MESSAGES, type MessageKey} from './messages';

// The browser edge of localization: the vue-i18n instance and the reads of
// localStorage and navigator. Workers code imports the plain modules instead.

export const i18n = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE,
    fallbackLocale: DEFAULT_LOCALE,
    messages: MESSAGES,
});

export function readBrowserLocaleState(): {storedLocale: Locale | null; languages: readonly string[]} {
    return {
        // Accessing the localStorage global itself throws where storage is blocked.
        storedLocale: readStoredLocale({getItem: (key) => localStorage.getItem(key)}),
        languages: navigator.languages?.length ? navigator.languages : [navigator.language],
    };
}

export function browserPreferredLocale(): Locale {
    return resolvePreferredLocale(readBrowserLocaleState());
}

export function setActiveLocale(locale: Locale): void {
    i18n.global.locale.value = locale;
    document.documentElement.lang = locale;
}

export function activeLocale(): Locale {
    return i18n.global.locale.value as Locale;
}

/**
 * A message in the active language, for code outside components that publishes text
 * (notifications, confirm dialogs). Logic modules return keys; this is where they become text.
 */
export function translate(key: MessageKey, values: Record<string, string | number> = {}): string {
    return i18n.global.t(key, values);
}

/** An internal link in the active language; templates re-render when the language changes. */
export function localeHref(href: string): string {
    return localizeHref(href, activeLocale());
}

/** A documentation link in the active language, English until that language's docs are published. */
export function localeDocsHref(docPath: string): string {
    return localizeDocsPath(docPath, activeLocale());
}
